// ruleEngine.js
//
// Pure, deterministic camera-settings rule engine.
// No network calls, no LLM, no randomness. Same inputs -> same outputs, always.
//
// Priority order (per spec):
// 1. Camera capability constraints
// 2. Exposure requirements
// 3. Frame-rate requirements
// 4. Shutter requirements
// 5. Lighting/ND requirements
// 6. ISO strategy
// 7. White balance
// 8. Picture profile
// 9. Stabilization
// 10. Focus
// 11. Creative preferences
//
// -----------------------------------------------------------------------
// Three conventions this file relies on, all of which live in cameras.json:
//
// 1. `videoModes` is the source of truth for what resolution/frame-rate
//    combinations actually exist. Resolution and frame rate are NOT picked
//    independently — that produced impossible recipes like "5.3K at 240fps."
//
// 2. `null` means "we don't know", `false` means "we know it doesn't have
//    this." They are never collapsed. Saying "not adjustable" about a
//    control we simply haven't confirmed is a factual claim we can't back.
//
// 3. `aperture` lets ND be calculated from exposure math instead of guessed
//    from a position in an array. Cameras with interchangeable lenses have
//    `aperture: null` and fall back to qualitative ND guidance.
// -----------------------------------------------------------------------

/**
 * cameras.json stores `videoModes` as the single source of truth. Older
 * callers (dropdowns, filters) expect flat `resolutions` / `frameRates`
 * arrays, so derive them here rather than duplicating the data in JSON,
 * where the two copies would drift apart the first time a camera is edited.
 *
 * Wrap the raw import once at load: `cameras.map(normalizeCamera)`.
 */
export function normalizeCamera(camera) {
  const caps = camera.capabilities || {};
  const modes = caps.videoModes || [];

  // Exposure-only cameras (imported from the Filtspot device list) carry
  // aperture, base ISO and ND class but no video-mode table. They are
  // still perfectly usable for the shutter/ISO/ND math — which is the
  // part of this engine that does the real work — so they normalize to
  // empty arrays rather than being dropped.
  if (!modes.length) {
    return {
      ...camera,
      capabilityLevel: camera.capabilityLevel || "exposure-only",
      capabilities: { ...caps, resolutions: caps.resolutions || [], frameRates: caps.frameRates || [] },
    };
  }

  const resolutions = modes.map((m) => m.resolution);
  const frameRates = [...new Set(modes.flatMap((m) => m.frameRates || []))].sort((a, b) => a - b);

  return {
    ...camera,
    capabilityLevel: camera.capabilityLevel || "full",
    capabilities: {
      ...caps,
      resolutions: caps.resolutions || resolutions,
      frameRates: caps.frameRates || frameRates,
    },
  };
}

function allFrameRates(camera) {
  const modes = camera.capabilities.videoModes || [];
  if (modes.length) return [...new Set(modes.flatMap((m) => m.frameRates || []))];
  return camera.capabilities.frameRates || [];
}

function closestSupported(target, supportedList) {
  if (!supportedList || supportedList.length === 0) return null;
  return supportedList.reduce((best, val) =>
    Math.abs(val - target) < Math.abs(best - target) ? val : best
  );
}

/**
 * Picks frame rate AND resolution together. `videoModes` is ordered
 * highest-quality-first, so the first mode that can actually do the chosen
 * frame rate is the best available one — no string-parsing of "5.3K" vs
 * "4K" to work out which is bigger.
 */
function pickMode(camera, scenario) {
  const modes = camera.capabilities.videoModes || [];
  const preferred = scenario.preferredFrameRates || [];
  const supported = allFrameRates(camera);

  // No video-mode table on file. Recommend the scenario's preferred frame
  // rate — that part is a property of the shot, not the camera — and say
  // plainly that we can't name a resolution rather than guessing one.
  if (!modes.length && !supported.length) {
    return {
      fps: preferred[0] || 30,
      resolution: "Not confirmed for this camera",
      modeNote: "We don't have this camera's resolution/frame-rate table on file. Use the highest resolution your camera offers at this frame rate, on most cameras the headline resolution is only available at the lowest rates.",
      assumed: true,
      resolutionCapped: false,
      resolutionKnown: false,
    };
  }

  for (const fps of preferred) {
    const mode = modes.find((m) => (m.frameRates || []).includes(fps));
    if (mode) {
      return { fps, resolution: mode.resolution, modeNote: mode.note || null, assumed: false, resolutionCapped: false, resolutionKnown: true };
    }
  }

  // No preferred rate is available at any resolution — fall back to the
  // closest rate this camera supports at all.
  const fallbackFps = closestSupported(preferred[0] || 30, supported);
  const fallbackMode = modes.find((m) => (m.frameRates || []).includes(fallbackFps));

  return {
    fps: fallbackFps,
    resolution: fallbackMode ? fallbackMode.resolution : (camera.capabilities.resolutions || []).slice(-1)[0] || "Not specified",
    modeNote: fallbackMode ? fallbackMode.note || null : null,
    assumed: true,
    resolutionCapped: !!fallbackMode && modes.indexOf(fallbackMode) > 0,
    resolutionKnown: true,
  };
}

function calcShutterSpeed(fps, shutterAngle, camera) {
  // Shutter time = shutterAngle / (360 * fps), so the shutter DENOMINATOR is
  // fps * (360 / shutterAngle). At the standard 180° angle this reduces to
  // 2 * fps: 24fps -> 1/48, 30fps -> 1/60, 60fps -> 1/120.
  const idealDenominator = fps * (360 / shutterAngle);
  const idealShutter = 1 / idealDenominator;

  const supportedOptions = camera.capabilities.shutterOptions;
  if (!supportedOptions) {
    // Camera shutter list unknown — return the calculated ideal, flagged as unverified
    return {
      display: `1/${Math.round(idealDenominator)}`,
      value: idealShutter,
      verified: false,
    };
  }

  // Parse "1/60" style strings back to denominators for comparison
  const parsed = supportedOptions.map((s) => ({
    label: s,
    denominator: parseFloat(s.split("/")[1]),
  }));
  const closest = parsed.reduce((best, cur) =>
    Math.abs(cur.denominator - idealDenominator) < Math.abs(best.denominator - idealDenominator)
      ? cur
      : best
  );
  return { display: closest.label, value: 1 / closest.denominator, verified: true };
}

function calcISO(camera, lighting) {
  const range = camera.capabilities.isoRange;
  const native = camera.capabilities.nativeISO;

  if (!range) {
    return { display: "Not specified by manufacturer", low: null, high: null, guidance: null, verified: false };
  }

  const [min, max] = range;
  const base = native || min;

  // `guidance` turns a flat range into a procedure: where to start, and the
  // condition under which it's OK to push higher — rather than a bare
  // number range that reads as "anything in here is equally fine."
  //
  // The "(base ISO)" label is only correct when the single value we landed
  // on IS the base. When a dim-light strategy gets clamped against the
  // camera's ceiling, low and high collapse to the *maximum* — calling that
  // "base ISO" was straightforwardly wrong.
  function withGuidance(low, high, verified) {
    if (low === high) {
      const atBase = low === base;
      return {
        display: atBase ? `${low} (base ISO)` : `${low} (this camera's maximum)`,
        low,
        high,
        guidance: atBase
          ? `Set it and leave it, this lighting has enough headroom that there's no reason to deviate from ${low}.`
          : `${low} is the highest this camera goes, so there's no headroom left. If the shot is still underexposed here, the fix is more light or a wider aperture, not more ISO.`,
        verified,
      };
    }
    return {
      display: `${low}–${high}`,
      low,
      high,
      guidance: `Start at ${low}. Only raise it toward ${high} if the shot is genuinely underexposed at ${low}, treat ${high} as a ceiling for this condition, not a default.`,
      verified,
    };
  }

  switch (lighting.isoStrategy) {
    case "base":
      return withGuidance(base, base, true);
    case "base-to-slightly-raised":
      return withGuidance(base, Math.min(base * 4, max), true);
    case "slightly-raised":
      return withGuidance(Math.min(base * 2, max), Math.min(base * 8, max), true);
    case "raised":
      return withGuidance(Math.min(base * 8, max), max, true);
    case "maximized":
      // Night sky / astro: there's so little light that ISO does all the work.
      // Sit near the top of the usable range rather than at base.
      return withGuidance(Math.min(base * 16, max), max, true);
    default:
      return withGuidance(base, base, true);
  }
}

// "ND16" -> 4 stops. ND1000 is sold as a 10-stop filter even though log2(1000)
// is 9.97, so round to the nearest tenth and let the comparison absorb it.
function ndStops(label) {
  const factor = parseFloat(String(label).replace(/[^0-9.]/g, ""));
  if (!factor || factor <= 1) return null;
  return Math.round(Math.log2(factor) * 10) / 10;
}

/**
 * ND, calculated rather than guessed.
 *
 * The old version picked `strengths[0..2]` by array index, so the answer
 * depended on how many filters happened to be listed for that camera —
 * adding a fourth ND to a camera's kit silently changed the recommendation
 * for every lighting condition. Worse, index 2 of the Mini 4 Pro's set is
 * ND256 (8 stops), which is roughly a stop and a half too strong for full
 * sun on that aircraft.
 *
 * The real relationship: a camera at aperture N, shutter t and ISO S is
 * metered at EV100 = log2(N²/t) - log2(S/100). If the scene is brighter
 * than that, the difference is how many stops of ND you need.
 *
 * Rounding goes UP deliberately. Over-filtering by half a stop is
 * recoverable with a small ISO bump; under-filtering is not recoverable at
 * all on a fixed-aperture camera already sitting at base ISO — the only
 * remaining control is a faster shutter, which breaks the 180° rule the
 * whole recipe is built around.
 */
function calcND(camera, lighting, shutter, iso) {
  const caps = camera.capabilities;

  if (caps.ndSupport === false) {
    return {
      needed: !!lighting.ndLikely,
      status: lighting.ndLikely ? "recommended-unsupported" : "not-needed",
      recommendation: lighting.ndLikely ? "Needed, but no native option" : "Not required",
      note: lighting.ndLikely ? caps.ndNote : null,
      requiredStops: null,
      // ndSupport: false is a confirmed fact in cameras.json (we know this
      // camera lacks native ND), not a guess — so this branch is verified.
      verified: true,
    };
  }

  if (caps.ndSupport === null || caps.ndSupport === undefined) {
    return {
      needed: !!lighting.ndLikely,
      status: "unknown",
      recommendation: "ND support for this camera isn't confirmed in our database",
      note: caps.ndNote || null,
      requiredStops: null,
      verified: false,
    };
  }

  const aperture = caps.aperture;
  const strengths = caps.ndStrengths || [];

  // No aperture on file (interchangeable-lens bodies) — the exposure math
  // can't be run, so stay qualitative instead of inventing a number.
  if (!aperture || !aperture.min) {
    return {
      needed: !!lighting.ndLikely,
      status: lighting.ndLikely ? "recommended-unknown-strength" : "not-needed",
      recommendation: lighting.ndLikely ? "Depends on your aperture" : "No ND needed",
      note: lighting.ndLikely
        ? `${caps.ndNote ? caps.ndNote + " " : ""}Because this body takes interchangeable lenses, the required strength changes with your aperture: meter at your working stop, then add whatever brings you back to the shutter speed above.`
        : null,
      requiredStops: null,
      verified: false,
    };
  }

  const isoForCalc = iso.low || caps.nativeISO || (caps.isoRange || [100])[0];
  const cameraEV100 = Math.log2((aperture.min * aperture.min) / shutter.value) - Math.log2(isoForCalc / 100);
  const overStops = Math.round((lighting.exposureStops - cameraEV100) * 10) / 10;

  if (overStops <= 0.5) {
    return {
      needed: false,
      status: "not-needed",
      recommendation: "No ND needed",
      note: null,
      requiredStops: overStops > 0 ? overStops : 0,
      verified: true,
    };
  }

  if (!strengths.length) {
    return {
      needed: true,
      status: "recommended-unknown-strength",
      recommendation: `About ${overStops} stops of ND (exact filters for this camera aren't in our database)`,
      note: "Adjust up or down one stop if the image still clips or looks underexposed.",
      requiredStops: overStops,
      verified: false,
    };
  }

  // Smallest filter that covers the requirement, rounding up.
  const ranked = strengths
    .map((label) => ({ label, stops: ndStops(label) }))
    .filter((s) => s.stops !== null)
    .sort((a, b) => a.stops - b.stops);

  const chosen = ranked.find((s) => s.stops >= overStops - 0.35) || ranked[ranked.length - 1];
  const shortfall = Math.round((overStops - chosen.stops) * 10) / 10;

  let note = "Adjust up or down one stop if the image still clips or looks underexposed.";
  if (shortfall > 0.5) {
    note = `${chosen.label} is the strongest filter in this camera's set and still leaves you roughly ${shortfall} stops over. Expect to raise shutter speed above the 180° ideal, or wait for softer light.`;
  } else if (shortfall < -0.5) {
    note = `${chosen.label} is about ${Math.abs(shortfall)} stops stronger than strictly needed. That's the safer direction to miss in, nudge ISO up a step to compensate rather than dropping to the weaker filter and having to raise shutter speed.`;
  }
  if (aperture.variable && aperture.max > aperture.min) {
    note += ` This camera also has a variable aperture (f/${aperture.min}–f/${aperture.max}), so stopping down is an alternative to a stronger filter, at some cost in diffraction softness at the narrow end.`;
  }

  return {
    needed: true,
    status: "recommended",
    recommendation: chosen.label,
    note,
    requiredStops: overStops,
    chosenStops: chosen.stops,
    aperture: aperture.min,
    verified: !!(aperture.verified !== false),
  };
}

function pickProfile(camera, look, experienceLevel) {
  const profiles = camera.capabilities.profiles || [];
  const wantsLog = look.profilePreference === "log";
  const hasLog = profiles.some((p) => /log/i.test(p));

  if (wantsLog && hasLog && experienceLevel !== "beginner") {
    const logProfile = profiles.find((p) => /log/i.test(p));
    return { profile: logProfile, requiresGrading: true, verified: true };
  }

  if (wantsLog && hasLog && experienceLevel === "beginner") {
    // Beginners get the non-log default, with the log option surfaced as "advanced mode"
    const normalProfile = profiles.find((p) => !/log/i.test(p)) || profiles[0];
    return {
      profile: normalProfile,
      alternativeAdvanced: profiles.find((p) => /log/i.test(p)),
      requiresGrading: false,
      verified: true,
    };
  }

  const normalProfile = profiles.find((p) => /normal|standard/i.test(p)) || profiles[0] || "Not confirmed for this camera";
  return { profile: normalProfile, requiresGrading: false, verified: profiles.length > 0 };
}

// In-camera sharpness/contrast/saturation. The cinematography rule this
// follows: footage headed for a color grade (Log/Flat profiles) should be
// captured as neutral as possible — in-camera sharpening, contrast, and
// saturation are baked into the file and can't be removed later, and
// fight against a grade. Footage NOT being graded can use the camera's
// default (or, for a punchier social-media look, slightly boosted)
// settings. Only recommends a value for what a camera actually exposes —
// GoPro's Sharpness-only Protune controls don't get a fabricated
// contrast/saturation number just because DJI's Style menu has one.
function calcImageStyle(camera, requiresGrading, look) {
  const style = camera.capabilities.imageStyle;
  if (!style) {
    const unknown = { value: "Not confirmed for this camera", verified: false };
    return { sharpness: unknown, contrast: unknown, saturation: unknown, note: null };
  }

  function recommendFor(field) {
    if (!field) return { value: "Not confirmed for this camera", verified: false };

    // null means "we haven't confirmed either way" — which is a different
    // statement from "this camera doesn't have the control." Telling
    // someone their camera can't do something we simply haven't checked is
    // the kind of wrong that costs trust.
    if (field.adjustable === null || field.adjustable === undefined) {
      return { value: "Not confirmed, check your camera's menu", verified: false };
    }
    if (field.adjustable === false) {
      return { value: "Not adjustable on this camera", verified: !!field.verified };
    }
    if (field.type === "slider") {
      const range = field.range || "";
      const floor = /(-\d+)/.test(range) ? range.match(/(-\d+)/)[1] : "-3";
      if (requiresGrading) return { value: `${floor} (minimum)`, verified: field.verified };
      if (look.id === "social") return { value: "+1", verified: field.verified };
      return { value: "0 (neutral)", verified: field.verified };
    }
    if (field.type === "levels") {
      const levels = field.range || [];
      if (requiresGrading) return { value: levels[0] || "Lowest available", verified: field.verified };
      if (look.id === "social") return { value: levels[levels.length - 1] || "Highest available", verified: field.verified };
      return { value: levels[1] || levels[0] || "Default", verified: field.verified };
    }
    return { value: "Not confirmed, check your camera's menu", verified: false };
  }

  return {
    sharpness: recommendFor(style.sharpness),
    contrast: recommendFor(style.contrast),
    saturation: recommendFor(style.saturation),
    note: style.note || null,
  };
}

// SD card speed-class guidance based on resolution + frame rate.
// This is general industry rule-of-thumb (SD Association speed classes),
// NOT a manufacturer-confirmed bitrate table for a specific camera — we
// don't have per-camera bitrate data, so this always carries that caveat
// rather than pretending to be camera-verified.
function calcSDCard(resolution, fps, camera) {
  if (camera.capabilities.internalStorageOnly) {
    return {
      speedClass: "N/A, records to internal storage",
      minSustainedWriteMBps: null,
      note: "This camera has no memory card slot. Plan around the fixed internal capacity instead: offload between sessions rather than assuming you can swap cards in the field.",
    };
  }

  if (/not confirmed/i.test(resolution || "")) {
    return {
      speedClass: "V30 (UHS-I) minimum, V60 if you shoot 4K60 or above",
      minSustainedWriteMBps: 30,
      note: "We don't have this camera's recording modes on file, so this is the general safe floor rather than a figure matched to its bitrate. Step up to V60/V90 if you record above 4K30.",
    };
  }

  const isHighRes = /5\.3K|5\.7K|6K|8K/i.test(resolution || "");
  const is4K = /4K/i.test(resolution || "");

  if (isHighRes || (is4K && fps >= 100)) {
    return {
      speedClass: "V90 (UHS-II) for sustained work, V60 minimum",
      minSustainedWriteMBps: 90,
      note: "6K/8K and high-frame-rate 4K push bitrates that a V30 card will not hold under sustained recording, the symptom is a clip that stops on its own partway through. General resolution/frame-rate guidance, not a bitrate figure confirmed against this camera's spec sheet.",
    };
  }
  if (isHighRes || (is4K && fps >= 60)) {
    return {
      speedClass: "V60 (UHS-II)",
      minSustainedWriteMBps: 60,
      note: "High-bitrate 4K60 recording needs a card rated for sustained high-speed writes, a slower card will drop frames or stop recording mid-clip. General guidance, not a manufacturer-confirmed bitrate figure.",
    };
  }
  if (is4K) {
    return {
      speedClass: "V30 (UHS-I)",
      minSustainedWriteMBps: 30,
      note: "The standard minimum for 4K24–48 recording on most consumer cameras. General guidance, not a manufacturer-confirmed bitrate figure.",
    };
  }
  return {
    speedClass: "V10 (UHS-I) minimum, V30 recommended",
    minSustainedWriteMBps: 10,
    note: "Lower resolutions are less demanding, but a V30 card leaves headroom if you switch to a higher setting mid-shoot. General guidance, not a manufacturer-confirmed bitrate figure.",
  };
}

// Panning guidance: avoiding visible strobing/judder when panning at a
// given frame rate is a well-known cinematography rule of thumb, but the
// exact safe pan speed depends on focal length and sensor width, which
// aren't in our camera database — so this stays qualitative (tied to fps)
// rather than a precise degrees-per-second number we'd have to invent.
function calcPanningGuidance(movement, fps) {
  if (!movement || movement.id === "static") return null;

  return {
    guidance: `As a rule of thumb, avoid completing a full pan across the frame in under about 5–7 seconds at ${fps}fps, faster pans at this frame rate will visibly strobe or judder. If the shot needs a faster pan, a higher frame rate (if this camera supports it) reduces the strobing effect.`,
    verified: false,
  };
}

function calcFocus(camera, scenario) {
  const mode = camera.capabilities.focusMode;
  const note = camera.capabilities.focusNote || null;

  // Old behaviour treated `autofocus: null` (unknown) the same as `false`
  // and told GoPro owners to use "manual focus" on a camera that has no
  // focus ring and no manual focus mode at all.
  if (mode === "fixed") {
    return { recommendation: "Fixed focus, nothing to set", note: note || "This camera has a fixed-focus lens with a deep depth of field. Everything past roughly arm's length is sharp; there is no focus control to adjust." };
  }
  if (mode === "manual") {
    return { recommendation: "Manual focus", note };
  }
  if (mode === "autofocus") {
    const facePriority = (scenario.priority || []).includes("face");
    return {
      recommendation: facePriority ? "Continuous AF with face/subject tracking" : "Continuous AF",
      note,
    };
  }
  return { recommendation: "Focus behaviour not confirmed for this camera", note };
}

function calcStabilization(camera, movement) {
  const caps = camera.capabilities;
  if (caps.stabilization === false) {
    return { available: false, recommendation: "N/A, no in-camera stabilization" };
  }
  if (caps.stabilization === null || caps.stabilization === undefined) {
    return { available: null, recommendation: "Stabilization options not confirmed for this camera" };
  }

  const levels = caps.stabilizationLevels || [];
  const heavyMotion = ["fast-action", "walking", "vehicle", "tracking"].includes(movement.id);

  if (movement.id === "static") {
    return {
      available: true,
      // Short value, long explanation separately — a stabilization cell
      // reading "Off if you're locked off on a tripod; 3-axis mechanical
      // gimbal (360° rotating) if you're holding it" is a sentence, not a
      // setting.
      recommendation: "Off on a tripod",
      note: levels.length
        ? `Switch to ${levels[0]} if you're handholding rather than locked off, on a tripod, stabilization can introduce drift by correcting for motion that isn't there.`
        : "Turn it on if you're handholding rather than locked off.",
    };
  }
  return {
    available: true,
    recommendation: heavyMotion
      ? levels[0] || "Highest available mode"
      : levels[0] || "Standard mode",
    note: null,
  };
}

/**
 * Generate a deterministic settings recipe.
 * @param {object} params
 * @param {object} params.camera - full camera object from cameras.json (pass through normalizeCamera first)
 * @param {object} params.scenario - full scenario object from scenarios.json
 * @param {object} params.lighting - full lighting object from lighting.json
 * @param {object} params.movement - full movement object from movement.json
 * @param {object} params.look - full look object from looks.json
 * @param {object} [params.output] - full output object from output.json (optional — platform/aspect-ratio context)
 * @param {string} [params.experienceLevel] - "beginner" | "advanced"
 */
export function generateRecipe({ camera, scenario, lighting, movement, look, output = null, experienceLevel = "beginner" }) {
  const assumptions = [];

  const mode = pickMode(camera, scenario);
  if (mode.assumed && mode.resolutionKnown === false) {
    assumptions.push("This camera is in our database for exposure only, we have its aperture and base ISO but not its resolution and frame-rate table, so resolution isn't specified here.");
  } else if (mode.assumed) {
    assumptions.push(`Scenario's preferred frame rate isn't supported by this camera; used the closest available (${mode.fps}fps) instead.`);
  }
  if (mode.resolutionCapped) assumptions.push(`This camera can't hit ${mode.fps}fps at its maximum resolution, so the recipe drops to ${mode.resolution}, which can.`);

  const shutter = calcShutterSpeed(mode.fps, scenario.shutterAngle, camera);
  if (!shutter.verified) assumptions.push("This camera's exact supported shutter speeds aren't in our database, shutter shown is the mathematically ideal value, not confirmed against the camera's menu options.");

  const iso = calcISO(camera, lighting);
  if (!iso.verified) assumptions.push("Manufacturer ISO range not confirmed in our database.");

  const nd = calcND(camera, lighting, shutter, iso);
  if (!nd.verified) assumptions.push("ND recommendation is qualitative, this camera's aperture or filter set isn't confirmed in our database, so we can't calculate an exact strength.");

  const profile = pickProfile(camera, look, experienceLevel);
  if (!profile.verified) assumptions.push("Picture profile list not confirmed for this camera.");

  const imageStyle = calcImageStyle(camera, profile.requiresGrading, look);
  const imageStyleUnverified = [imageStyle.sharpness, imageStyle.contrast, imageStyle.saturation].some((f) => !f.verified);
  if (imageStyleUnverified) assumptions.push("This camera's sharpness/contrast/saturation controls aren't fully confirmed in our database.");

  const provisional = camera.dataConfidence === "provisional";
  if (provisional) assumptions.push("This camera is recent enough that its published specifications haven't been cross-checked against a manufacturer spec sheet yet, treat these numbers as a starting point and verify against your own menu.");

  const wb = camera.capabilities.manualWhiteBalance
    ? { display: lighting.wbLabel, kelvin: lighting.wbKelvin, mode: "Manual" }
    : { display: "Auto (manual WB not available on this camera)", kelvin: null, mode: "Auto" };

  // Some lighting labels are a full explanation rather than a value —
  // mixed lighting's runs to 180 characters. That's the right text for
  // prose and the wrong text for a table cell or a phone card, where a
  // paragraph in the value column stops being a readable setting.
  // So: a short form for anywhere a VALUE is expected, with the full
  // sentence carried separately as a note.
  function shortWhiteBalance(label) {
    if (wb.mode === "Auto") return "Auto";
    const firstClause = String(label).split(/\s+—\s+|\.\s+/)[0].trim();
    return firstClause.length <= 40 ? firstClause : `${firstClause.slice(0, 37).trimEnd()}…`;
  }

  const whiteBalanceShort = shortWhiteBalance(wb.display);
  // Only surface a note when the short form actually dropped something.
  const whiteBalanceNote = wb.display !== whiteBalanceShort ? wb.display : null;

  // A formatted, always-present color-temperature value — separate from
  // `wb.display` (which is the prose label like "Daylight (5600K)") so the
  // Kelvin number itself is its own field rather than only ever appearing
  // embedded in a sentence. lighting.wbKelvin is null for mixed-lighting
  // (genuinely no single correct value, not a data gap), which is called
  // out explicitly rather than left blank.
  const colorTemperature = wb.mode === "Auto"
    ? "N/A, camera set to Auto white balance"
    : wb.kelvin
    ? `${wb.kelvin}K`
    : "No single value, mixed light sources (see white balance note)";

  const stabilization = calcStabilization(camera, movement);
  const focus = calcFocus(camera, scenario);
  const sdCard = calcSDCard(mode.resolution, mode.fps, camera);
  const panning = calcPanningGuidance(movement, mode.fps);

  // Confidence scoring: start at 1.0, deduct for each unverified/assumed component
  let confidence = 1.0;
  if (mode.assumed) confidence -= 0.15;
  if (!shutter.verified) confidence -= 0.2;
  if (!iso.verified) confidence -= 0.15;
  if (!nd.verified) confidence -= 0.15;
  if (!profile.verified) confidence -= 0.1;
  if (imageStyleUnverified) confidence -= 0.05;
  if (provisional) confidence -= 0.1;
  confidence = Math.max(0, Math.round(confidence * 100) / 100);

  return {
    camera: camera.name,
    scenario: scenario.name,
    lighting: lighting.name,
    movement: movement.name,
    look: look.name,
    settings: {
      resolution: mode.resolution,
      resolutionNote: mode.modeNote,
      resolutionKnown: mode.resolutionKnown !== false,
      fps: mode.fps,
      shutter: shutter.display,
      shutterAngle: scenario.shutterAngle,
      iso: iso.display,
      isoLow: iso.low,
      isoGuidance: iso.guidance,
      whiteBalance: wb.display,
      whiteBalanceShort,
      whiteBalanceNote,
      whiteBalanceMode: wb.mode,
      colorTemperature,
      colorTemperatureKelvin: wb.kelvin,
      profile: profile.profile,
      profileAlternative: profile.alternativeAdvanced || null,
      requiresGrading: profile.requiresGrading,
      sharpness: imageStyle.sharpness.value,
      contrast: imageStyle.contrast.value,
      saturation: imageStyle.saturation.value,
      imageStyleNote: imageStyle.note,
      ndFilter: nd.recommendation,
      ndNote: nd.note,
      // Structured ND facts, so the explanation layer can branch on a value
      // instead of string-matching the display text. The old code compared
      // against the literal "No ND needed" and produced sentences like
      // "Not required brings the amount of light hitting the sensor back
      // down..." for every camera without native ND support.
      ndStatus: nd.status,
      ndNeeded: nd.needed,
      ndAperture: nd.aperture || null,
      // A compact label for meta descriptions and card summaries, where the
      // full sentence-length recommendation doesn't fit.
      ndShort:
        nd.status === "recommended" ? nd.recommendation
        : nd.status === "not-needed" ? "no ND"
        : nd.status === "recommended-unsupported" ? "third-party ND needed"
        : "ND strength varies",
      ndRequiredStops: nd.requiredStops,
      ndChosenStops: nd.chosenStops || null,
      stabilization: stabilization.recommendation,
      stabilizationNote: stabilization.note || null,
      focus: focus.recommendation,
      focusNote: focus.note,
      aspectRatio: output ? output.aspectRatio : null,
      outputNote: output ? output.notes : null,
      outputName: output ? output.name : null,
    },
    // Bonus/pro-tier fields — intentionally kept OUT of `settings` above.
    // `settings` is what the public, indexed recipe page renders; `bonus`
    // is only ever read by the gated (access-code) mobile view. Keeping
    // them as separate top-level keys means a component would have to
    // deliberately reach into `recipe.bonus` to show this — it can't leak
    // onto the public page by accident.
    //
    // Worth being clear-eyed about what that does and doesn't buy you: it
    // stops accidental rendering, not determined access. The gate is
    // client-side and this data ships inside the JS bundle either way.
    bonus: {
      sdCard,
      panning,
    },
    confidence,
    assumptions,
  };
}
